/**
 * Pure Plotly builders for the High-Frequency SVAR tab.
 *
 * Each financial variable's move is decomposed into six structural shocks.
 * The source CSV is cumulative (contribution since the window's base week);
 * the week-over-week view is its first difference. Both render as a relative
 * (signed-stacked) bar chart with a total-move line overlay.
 *
 * Kept separate from `SvarTab.tsx` so the data shaping can be unit-tested.
 */
import type { Data, Layout } from 'plotly.js';
import { mergeLayout } from '@/lib/plotly/defaults';
import { chartLayout } from '@/lib/plotly/layoutPresets';
import { hoverTemplate } from '@/lib/plotly/hoverTemplates';
import type { SvarMode, SvarShock, SvarVariable } from '../types';

/** Structural shocks in stacking order, with display label + colour. */
export const SVAR_SHOCKS: ReadonlyArray<{ key: SvarShock; label: string; color: string }> = [
  { key: 'MP', label: 'Monetary Policy', color: '#729dcd' },
  { key: 'RISK', label: 'Risk', color: '#ec665c' },
  { key: 'OIL', label: 'Oil', color: '#193947' },
  { key: 'DEMAND', label: 'Demand', color: '#7ACAC5' },
  { key: 'SUPPLY', label: 'Supply', color: '#ff9d4a' },
  { key: 'RESIDUAL', label: 'Residual', color: '#b0b7bd' },
];

/** First difference of a cumulative series (week-over-week contribution). */
function diff(values: number[]): number[] {
  return values.map((v, i) => (i === 0 ? 0 : v - values[i - 1]));
}

/** Per-shock series for the chosen view; cumulative is the raw CSV. */
export function shockSeries(
  variable: SvarVariable,
  mode: SvarMode,
): Record<SvarShock, number[]> {
  const out = {} as Record<SvarShock, number[]>;
  for (const { key } of SVAR_SHOCKS) {
    const raw = variable.series[key] ?? [];
    out[key] = mode === 'wow' ? diff(raw) : raw;
  }
  return out;
}

/** Hover suffix for a variable's unit — matches the y-axis label format. */
function unitSuffix(unit: SvarVariable['unit']): string {
  return unit === '%' ? '%' : ' bps';
}

/** Stacked-bar traces (one per shock) + a total-move line for one variable. */
export function buildSvarTraces(variable: SvarVariable, mode: SvarMode): Data[] {
  const series = shockSeries(variable, mode);
  const x = variable.dates;
  const suffix = unitSuffix(variable.unit);

  const traces: Data[] = SVAR_SHOCKS.map(({ key, label, color }) => ({
    type: 'bar',
    x,
    y: series[key],
    name: label,
    marker: { color },
    hovertemplate: hoverTemplate({ name: label, format: '.3f', suffix }),
  }));

  // Total move = sum of all six shock contributions at each date.
  const total = x.map((_, i) =>
    SVAR_SHOCKS.reduce((sum, { key }) => sum + (series[key][i] ?? 0), 0),
  );
  traces.push({
    type: 'scatter',
    mode: 'lines+markers',
    x,
    y: total,
    name: 'Total move',
    line: { color: '#111827', width: 2 },
    marker: { size: 6, symbol: 'diamond', color: '#111827' },
    hovertemplate: hoverTemplate({ name: 'Total move', format: '.3f', suffix }),
  });

  return traces;
}

/** Layout for the SVAR decomposition chart. */
export function buildSvarLayout(variable: SvarVariable, mode: SvarMode): Partial<Layout> {
  const kind = mode === 'wow' ? 'Weekly' : 'Cumulative';
  // Mirrors the upstream PNG y-axis labels: "Cumulative % Change" /
  // "Cumulative bps Change" (and the WoW equivalents).
  const yTitle = `${kind} ${variable.unit} Change`;
  return mergeLayout({
    ...chartLayout({
      family: 'bar',
      title: `${variable.label} — ${kind} Shock Decomposition`,
    }),
    barmode: 'relative',
    yaxis: { title: { text: yTitle, standoff: 10 }, zeroline: true },
    xaxis: { title: { text: '', standoff: 10 } },
    hovermode: 'x unified',
  });
}
